import { useCallback, useEffect, useMemo, useState } from 'react'

const AGGREGATE_FNS = {
  sum: (values) => values.reduce((a, b) => a + b, 0),
  avg: (values) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0,
  min: (values) => values.length ? Math.min(...values) : 0,
  max: (values) => values.length ? Math.max(...values) : 0,
  count: (values) => values.length,
  first: (values) => values[0] ?? null,
  last: (values) => values[values.length - 1] ?? null
}

function sanitizeAccessorKey (value) {
  return String(value).replace(/[.[\]\\]/g, '_')
}

function buildCompositeKey (row, columns) {
  return columns.map(col => row[col] ?? '').join('\x00')
}

/**
 * Extract distinct pivot values and optionally group them by a parent column.
 * Returns flat column descriptors and, when grouped, parent/child relationships
 * compatible with useCollapsibleColumns.
 */
function buildPivotColumns (rows, pivotConfig) {
  const { pivotColumn, sortPivotColumns, enableColumnGrouping, groupColumn } = pivotConfig

  const seen = new Map()
  for (const row of rows) {
    const val = row[pivotColumn]
    if (val == null) continue
    const key = String(val)
    if (!seen.has(key)) {
      seen.set(key, {
        value: key,
        groupValue: enableColumnGrouping && groupColumn ? row[groupColumn] ?? null : null
      })
    }
  }

  const entries = [...seen.values()]

  if (sortPivotColumns === 'alpha') {
    entries.sort((a, b) => a.value.localeCompare(b.value))
  } else if (sortPivotColumns === 'value') {
    entries.sort((a, b) => {
      const aNum = Number(a.value)
      const bNum = Number(b.value)
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
      return a.value.localeCompare(b.value)
    })
  }
  // 'ordinal': no sort — preserves Map insertion order (the order values first appear in the data)

  const flatColumns = entries.map(e => {
    const safeKey = sanitizeAccessorKey(e.value)
    return {
      id: `_pivot_${safeKey}`,
      accessor: `_pivot_${safeKey}`,
      header: () => e.value,
      pivotValue: e.value,
      groupValue: e.groupValue
    }
  })

  if (!enableColumnGrouping || !groupColumn) {
    return flatColumns
  }

  const grouped = new Map()
  const groupOrder = []
  for (const col of flatColumns) {
    const gv = col.groupValue ?? '__ungrouped'
    if (!grouped.has(gv)) {
      grouped.set(gv, { parentName: gv === '__ungrouped' ? 'Other' : String(gv), children: [] })
      groupOrder.push(gv)
    }
    grouped.get(gv).children.push(col)
  }

  const result = []
  for (const gv of groupOrder) {
    const group = grouped.get(gv)
    const safeGroupKey = sanitizeAccessorKey(gv)
    const groupId = `_pivot_group_${safeGroupKey}`
    const childIds = group.children.map(c => c.id)

    result.push({
      id: groupId,
      accessor: groupId,
      header: () => group.parentName,
      isGroup: true,
      groupId,
      children: childIds,
      isCollapsible: true
    })

    for (const child of group.children) {
      result.push({ ...child, parentGroupId: groupId, isGroup: false })
    }
  }

  return result
}

/**
 * Re-bucket flat rows so that each unique identity (all columns except the
 * pivot and value columns) produces one output row with the value column
 * spread across dynamic pivot keys.
 */
function rebucketRows (rows, pivotConfig) {
  const { pivotColumn, valueColumn } = pivotConfig
  const bucketMap = new Map()
  const bucketOrder = []

  for (const row of rows) {
    const pivotVal = row[pivotColumn]
    if (pivotVal == null) continue

    const identityParts = []
    const identityRow = {}
    for (const [k, v] of Object.entries(row)) {
      if (k === pivotColumn || k === valueColumn) continue
      identityParts.push(`${k}=${v}`)
      identityRow[k] = v
    }
    const identityKey = identityParts.join('\x00')

    if (!bucketMap.has(identityKey)) {
      bucketMap.set(identityKey, { ...identityRow })
      bucketOrder.push(identityKey)
    }

    const bucket = bucketMap.get(identityKey)
    const colId = `_pivot_${sanitizeAccessorKey(pivotVal)}`
    const val = Number(row[valueColumn])
    bucket[colId] = isNaN(val) ? null : val
  }

  return bucketOrder.map(key => bucketMap.get(key))
}

function computeTotals (rows, pivotColumns) {
  const pivotIds = pivotColumns.filter(c => !c.isGroup).map(c => c.id)
  for (const row of rows) {
    let total = 0
    let hasValue = false
    for (const id of pivotIds) {
      const v = row[id]
      if (typeof v === 'number' && !isNaN(v)) {
        total += v
        hasValue = true
      }
    }
    row._total = hasValue ? total : null
  }
  return rows
}

function computeAllocations (treeData, pivotColumns) {
  const pivotIds = pivotColumns.filter(c => !c.isGroup).map(c => c.id)
  function walk (nodes, parentTotal) {
    for (const node of nodes) {
      if (typeof parentTotal === 'number' && parentTotal !== 0 && typeof node._total === 'number') {
        node._allocation = node._total / parentTotal
      } else if (typeof parentTotal === 'number' && parentTotal === 0) {
        node._allocation = 0
      }
      const nodeTotal = node._total
      for (const id of pivotIds) {
        const v = node[id]
        if (typeof v === 'number' && typeof nodeTotal === 'number' && nodeTotal !== 0) {
          node[`${id}_alloc`] = v / nodeTotal
        } else if (typeof v === 'number' && typeof nodeTotal === 'number' && nodeTotal === 0) {
          node[`${id}_alloc`] = 0
        }
      }
      if (node.subRows?.length) {
        walk(node.subRows, node._total)
      }
    }
  }
  walk(treeData, null)
}

function aggregateTree (tree, pivotColumns, aggregateFn, includeTotal) {
  const fn = AGGREGATE_FNS[aggregateFn] || AGGREGATE_FNS.sum
  const pivotIds = pivotColumns.filter(c => !c.isGroup).map(c => c.id)
  const allIds = includeTotal ? [...pivotIds, '_total'] : pivotIds

  function walk (node) {
    if (!node.subRows?.length) return node

    const children = node.subRows.map(walk)
    const aggregated = { ...node, subRows: children }

    for (const colId of allIds) {
      const childValues = []
      for (const child of children) {
        const v = child[colId]
        if (typeof v === 'number' && !isNaN(v)) childValues.push(v)
      }
      if (childValues.length) {
        aggregated[colId] = fn(childValues)
      }
    }
    return aggregated
  }

  return tree.map(walk)
}

// Group column headers always sum their child pivot columns regardless of the
// tree-level aggregate function. The group header answers "total across all
// members of this group" (e.g. total across all accounts in a category).
function addGroupColumnTotals (rows, pivotColumns) {
  const parentCols = pivotColumns.filter(c => c.isGroup)
  if (!parentCols.length) return

  function walk (row) {
    for (const parentCol of parentCols) {
      const existing = row[parentCol.accessor]
      if (existing == null) {
        const childValues = parentCol.children
          .map(childId => row[childId])
          .filter(val => typeof val === 'number' && !isNaN(val))
        row[parentCol.accessor] = childValues.length
          ? childValues.reduce((sum, val) => sum + val, 0)
          : null
      }
    }
    if (row.subRows?.length) {
      row.subRows.forEach(walk)
    }
  }

  rows.forEach(walk)
}

function groupAtLevel (rows, levels, levelIndex, labelColumn, leafLabelAccessor) {
  if (levelIndex >= levels.length) {
    return rows.map(row => {
      const leaf = { ...row, _isLeaf: true }
      if (labelColumn && leafLabelAccessor) {
        leaf[labelColumn] = row[leafLabelAccessor] ?? null
      }
      return leaf
    })
  }

  const { groupBy, labelAccessor } = levels[levelIndex]
  const groupMap = new Map()
  const groupOrder = []

  for (const row of rows) {
    const key = buildCompositeKey(row, groupBy)
    if (!groupMap.has(key)) {
      groupMap.set(key, [])
      groupOrder.push(key)
    }
    groupMap.get(key).push(row)
  }

  return groupOrder.map(key => {
    const groupRows = groupMap.get(key)
    const representative = groupRows[0]
    const subRows = groupAtLevel(groupRows, levels, levelIndex + 1, labelColumn, leafLabelAccessor)

    const labelValue = representative[labelAccessor] ?? key
    const node = {
      _isGroupNode: true,
      _depth: levelIndex,
      _label: labelValue
    }

    if (labelColumn) {
      node[labelColumn] = labelValue
    }

    for (const col of groupBy) {
      node[col] = representative[col]
    }
    if (labelAccessor && !(labelAccessor in node)) {
      node[labelAccessor] = representative[labelAccessor]
    }

    node.subRows = subRows
    return node
  })
}

function buildInitialExpandState (tree, defaultExpandDepth, currentDepth = 0) {
  const state = {}
  if (currentDepth >= defaultExpandDepth) return state

  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node.subRows?.length) {
      const rowId = String(i)
      state[rowId] = true
      const childState = buildInitialExpandState(node.subRows, defaultExpandDepth, currentDepth + 1)
      for (const [childKey, val] of Object.entries(childState)) {
        state[`${rowId}.${childKey}`] = val
      }
    }
  }
  return state
}

/**
 * Transforms flat blueprint rows into a hierarchical pivoted tree.
 *
 * 1. Extract distinct pivot column values as dynamic columns
 * 2. Re-bucket rows: spread valueColumn across pivot column keys
 * 3. Build tree via composite-key grouping
 * 4. Aggregate pivot values up the tree (client-side)
 * 5. Compute group column totals when column grouping is enabled
 * 6. Optionally compute allocation percentages
 *
 * @param {Array<Object>} flatRows - flat row data from blueprint execution
 * @param {Object} hierarchyConfig - hierarchy configuration from presentation config
 * @param {Object} pivotConfig - pivot configuration from presentation config
 */
export default function useBlueprintPivotTreeData (flatRows, hierarchyConfig, pivotConfig) {
  const {
    levels = [],
    defaultExpandDepth = 1,
    labelColumn = '_hierarchyLabel',
    leafLabelAccessor = null
  } = hierarchyConfig || {}

  const {
    aggregateFunction = 'sum',
    showTotal = true,
    showAllocation = false,
    enableColumnGrouping = false
  } = pivotConfig || {}

  const pivotColumns = useMemo(() => {
    if (!flatRows?.length || !pivotConfig) return []
    return buildPivotColumns(flatRows, pivotConfig)
  }, [flatRows, pivotConfig])

  const treeData = useMemo(() => {
    if (!flatRows?.length || !levels.length || !pivotConfig) return []

    const rebucketed = rebucketRows(flatRows, pivotConfig)
    if (showTotal !== false) {
      computeTotals(rebucketed, pivotColumns)
    }

    let tree = groupAtLevel(rebucketed, levels, 0, labelColumn, leafLabelAccessor)
    tree = aggregateTree(tree, pivotColumns, aggregateFunction, showTotal !== false)

    if (enableColumnGrouping) {
      addGroupColumnTotals(tree, pivotColumns)
    }

    if (showAllocation) {
      computeAllocations(tree, pivotColumns)
    }

    return tree
  }, [flatRows, levels, pivotConfig, pivotColumns, labelColumn, leafLabelAccessor, aggregateFunction, showTotal, enableColumnGrouping, showAllocation])

  const initialExpandState = useMemo(() => {
    return buildInitialExpandState(treeData, defaultExpandDepth)
  }, [treeData, defaultExpandDepth])

  const [expandState, setExpandState] = useState(initialExpandState)

  useEffect(() => {
    setExpandState(initialExpandState)
  }, [initialExpandState])

  const resetExpandState = useCallback(() => {
    setExpandState(initialExpandState)
  }, [initialExpandState])

  return {
    treeData,
    pivotColumns,
    expandState,
    setExpandState,
    resetExpandState
  }
}
